import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/practice :: PracticeEmptyState
 *
 * Shown when `/api/practice/next` returns 404 with body
 * `{status: "no_questions_available"}`. Possible causes:
 *
 *   1. Seed bank exhausted for the (state, locale) combo.
 *   2. Every question has been answered correctly within the 7-day
 *      exclusion window (see CORRECT_EXCLUSION_WINDOW_MS in PR #111).
 *
 * Either way the copy nudges the user back to their dashboard where
 * they can pick a different focus state or wait.
 */
export interface PracticeEmptyStateProps {
  stateCode: string;
}

export default function PracticeEmptyState({
  stateCode,
}: PracticeEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <section
      data-testid="practice-empty-state"
      role="status"
      aria-live="polite"
      className="mt-md rounded-lg border border-brand-primary/10 bg-brand-surface/40 p-lg text-center"
    >
      <h2 className="font-display text-xl font-bold text-brand-primary">
        {t("practice.empty.heading")}
      </h2>
      <p className="mt-sm text-sm text-neutral-800">
        {t("practice.empty.body", { state: stateCode })}
      </p>
      <Link
        to="/dashboard"
        data-testid="practice-empty-dashboard-link"
        className="mt-md inline-flex items-center rounded-md border border-brand-primary bg-white px-md py-sm text-sm font-semibold text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("practice.empty.backToDashboard")}
      </Link>
    </section>
  );
}
